import { ReportActionBtnContainer } from "../../../screens/User/Users/components/Roles/style";
import { RiArrowGoBackFill } from "react-icons/ri";
import { IoMdCloseCircleOutline } from "react-icons/io";
import DropdownMenu from "../../Dropdown";
import ActionBtnU from "../../../screens/User/Users/components/ActionBtnU";
import { usePermission } from "../../../hooks/usePermission";
import { Fragment, useEffect, useState } from "react";
import { PERMISSION_LIST } from "../../../shared/sharedConstants";
import { IncidentStatus } from "../../../types/fieldInstance.interface";
import AlertDialog from "../../../screens/User/Users/components/AlertDialog/AlertDialog";
import { useIncidentFormContext } from "./IncidentContext";
import { useIncidentData } from "../../../layouts/incidentLayout";
import { useFormikContext } from "formik";
import { markIncidentReadyForApproval } from "../../../api/incident/apiIncidents";
import { queryClient } from "../../../App";
import { message } from "antd";
import {
  closeIncidentCreation,
  setIncidentStatusToOpen,
} from "./methodsAndHandlers";
import styled from "styled-components";
import { trackButtonClick } from "../../../utils/analytics";

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  @media screen and (min-width: 961px) {
    margin-right: 1rem;
  }
  @media screen and (max-width: 435px) {
    margin-top: 1rem;
  }
`;
const OtherBtnsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: 435px) {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
    gap: 1rem;
    width: 100%;
    margin-bottom: 2rem;
  }
`;
const FirstBtnContainer = styled.div`
  display: flex;
  gap: 8px;
  @media screen and (min-width: 961px) {
    margin-left: 0.25rem;

    margin-right: 0.25rem;
  }
`;
const Spacer = styled.div`
  @media screen and (min-width: 961px) {
    margin-left: 0.75rem;
    margin-right: 0.75rem;
  }
`;

interface IButtonGroupProps {
  saveIncidentForm?: () => void;
  isCreateButtonDisabled: boolean;
  shouldShowSaveButton: boolean;
  attachFiles?: () => void;
  handleOpenModal?: (x: boolean) => void;
  getSubmitButtonName: (color?: string, radius?: number) => any;
  incidentStatus?: IncidentStatus;
  isAttachments?: boolean;
  canUserAddAttachments?: boolean;
  setAttachmentsMode?: (x: any) => void;
  attachmentsMode?: boolean;
  handleDiscardFromAttachments?: () => void;
  tabTitle?: string;
}

const ButtonGroup = ({
  saveIncidentForm,
  isCreateButtonDisabled,
  shouldShowSaveButton,
  attachFiles,
  handleOpenModal,
  getSubmitButtonName,
  incidentStatus,
  isAttachments,
  canUserAddAttachments,
  setAttachmentsMode,
  attachmentsMode,
  handleDiscardFromAttachments,
  tabTitle,
}: IButtonGroupProps) => {
  const {
    shouldAllowInput: allowInput,
    canUserEditPostSubmission,
    setShouldStayOnPage,
    hasUnsavedChanges,
    setSearchParams,
    _handleDiscard,
  } = useIncidentFormContext();

  const formik = useFormikContext<{ [x: string]: any }>();

  const {
    userId,
    selectedIncident,
    saveIncidentLoading,
    isDeletingIncident,
    setIncidentFormState,
    handleDeleteIncident,
    restoreIncident,
    setStatusLoading,
    setStatusAsync,
  } = useIncidentData();

  const qapiOptions = [
    {
      name: "QAPI",
      style: "",
      value: "qapi",
    },
    {
      name: "NON-QAPI",
      style: "",
      value: "nonqapi",
    },
  ];
  // MARCC-771: default the print to NON-QAPI (select by value so reordering options is safe).
  // QAPI must stay options[0] so Dropdown can keep deriving the QAPI value from options[0].
  const defaultOption =
    qapiOptions.find((option) => option.value === "nonqapi")?.value ??
    qapiOptions[1].value;

  const { isPermitted } = usePermission();

  const [showPrint, setShowPrint] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showMarkReadyConfirm, setShowMarkReadyConfirm] = useState(false);
  const [markReadyLoading, setMarkReadyLoading] = useState(false);

  useEffect(() => {
    setShowPrint(
      isPermitted(PERMISSION_LIST.VIEW_REPORT, selectedIncident?.Facility__c)
    );
  }, [selectedIncident?.Id]);

  function disableSaveButton() {
    if (saveIncidentLoading) {
      return true;
    }
    return !allowInput;
  }

  const _handleDeleteIncident = () => {
    handleDeleteIncident({ id: selectedIncident?.Id, loggedInUserId: userId });
    setShowDialog(false);
  };

  const _handleRestoreIncident = () => {
    restoreIncident.mutate({ incidentId: selectedIncident?.Id, userId });
  };

  function setEditModeActive(value: boolean) {
    if (hasUnsavedChanges && !value) {
      setShouldStayOnPage(true);
      setIncidentFormState({ showPrompt: true });
    } else {
      isAttachments && setAttachmentsMode
        ? setAttachmentsMode({ mode: value ? "edit" : "view" })
        : setSearchParams({ mode: value ? "edit" : "view" });
    }
  }

  async function _setIncidentStatusToOpen() {
    setIncidentStatusToOpen(
      setStatusAsync,
      selectedIncident?.Id,
      userId,
      setIncidentFormState
    );
    setShowOpenDialog(false);
  }

  function _closeIncidentCreation() {
    closeIncidentCreation(
      hasUnsavedChanges,
      setIncidentFormState,
      isAttachments && handleDiscardFromAttachments
        ? handleDiscardFromAttachments
        : _handleDiscard
    );
  }

  function setOpenStatusDialog() {
    setShowOpenDialog(true);
  }

  const isOwnIncident = userId === selectedIncident?.Incident_Created_By__c;

  const isDeletedIncident =
    selectedIncident?.Enabled_Incident_For_Web_User__c === false;

  const areEditsPermitted = isAttachments
    ? canUserAddAttachments
    : canUserEditPostSubmission;

  // MARCC-776: signed/completed incidents are locked from re-editing/resubmitting (that path
  // created duplicates). Scoped to the incident form only — isAttachments stays editable so
  // attachments can still be added to completed incidents. Never true for a new incident.
  const isLocked =
    !isAttachments &&
    (selectedIncident?.Incident_Status__c === IncidentStatus.SignatoryInProgress ||
      selectedIncident?.Incident_Status__c === IncidentStatus.Completed);

  const allowEdits =
    ((!isDeletedIncident && areEditsPermitted) ||
      (isOwnIncident && incidentStatus === IncidentStatus.Open)) &&
    !isLocked;

  const isEditMode = isAttachments ? attachmentsMode : allowInput;

  const disableOpenButton =
    selectedIncident?.Incident_Status__c === IncidentStatus.Open ||
    setStatusLoading;

  const openButtonIsVisible =
    selectedIncident?.Incident_Status__c === IncidentStatus.Submitted &&
    areEditsPermitted;

  function deleteIncidentOrDraft() {
    if (incidentStatus === IncidentStatus.Draft && handleOpenModal) {
      handleOpenModal(true);
    } else {
      setShowDialog(true);
    }
  }

  async function handleConfirmMarkReady() {
    setMarkReadyLoading(true);
    try {
      await markIncidentReadyForApproval(selectedIncident?.Id);
      message.success("Incident marked as Ready for Approval");
      queryClient.invalidateQueries(["incidentsWithParams"]);
      queryClient.invalidateQueries(["incident", selectedIncident?.Id]);
      queryClient.invalidateQueries(["incidentDetailsWithId"]);
      setShowMarkReadyConfirm(false);
    } catch (err: any) {
      console.error("Error marking incident ready:", err);
      message.error(err?.response?.data?.message || "Failed to mark incident as Ready for Approval");
    } finally {
      setMarkReadyLoading(false);
    }
  }

  return (
    <ReportActionBtnContainer>
      <BackButtonContainer>
        <ActionBtnU
          borderStyle="1px solid rgb(181, 128, 255)"
          color="rgb(181, 128, 255)"
          onClick={() => {
            _closeIncidentCreation();
          }}
          data-testid="exit"
        >
          <span className="mr-2 mt-1 font-bold">
            <IoMdCloseCircleOutline />
          </span>
          Exit
        </ActionBtnU>
      </BackButtonContainer>
      <OtherBtnsContainer>
        {!isEditMode ? (
          <>
            {allowEdits && (
              <FirstBtnContainer>
                <ActionBtnU
                  borderStyle="1px solid rgb(181, 128, 255)"
                  color="rgb(181, 128, 255)"
                  disabled={isDeletingIncident}
                  data-testid="edit-btn"
                  type="button"
                  onClick={deleteIncidentOrDraft}
                >
                  Delete
                </ActionBtnU>
              </FirstBtnContainer>
            )}
            {incidentStatus !== IncidentStatus.Draft &&
              showPrint &&
              !isDeletedIncident && (
                <DropdownMenu
                  className="mr-3"
                  isReport
                  options={qapiOptions}
                  id={selectedIncident?.Id}
                  defaultOption={defaultOption}
                >
                  <ActionBtnU
                    // borderStyle="1px solid var(--primaryPurpleMain)"
                    borderStyle="1px solid rgb(181, 128, 255)"
                    color="rgb(181, 128, 255)"
                    data-testid="print-btn"
                    margin="0 0.25rem 0 0 !important"
                    type="button"
                  >
                    Print
                  </ActionBtnU>
                </DropdownMenu>
              )}

            {isDeletedIncident && (
              <Fragment>
                <p className="mr-4 mt-2 text-red-700">Deleted</p>
                <ActionBtnU
                  data-testid="restore-btn"
                  type="button"
                  onClick={() => setShowRestore(true)}
                  largeWidth="auto"
                  purpleBg
                  white_color
                >
                  Restore Incident
                </ActionBtnU>
              </Fragment>
            )}

            {allowEdits ? (
              <span className="ml-2 mr-2" style={{ display: "inline-flex", gap: 8 }}>
                <ActionBtnU
                  borderStyle="1px solid rgb(181, 128, 255)"
                  color="rgb(181, 128, 255)"
                  data-testid="edit-btn-conclusion"
                  type="button"
                  onClick={() => setEditModeActive(true)}
                >
                  Edit
                </ActionBtnU>
              </span>
            ) : null}
          </>
        ) : (
          <>
            {shouldShowSaveButton ? (
              <ActionBtnU
                data-testid="save-btn"
                className="ml-2 mr-2"
                onClick={saveIncidentForm}
                borderStyle="1px solid rgb(181, 128, 255)"
                color="rgb(181, 128, 255)"
                // smallWidth
                disabled={disableSaveButton()}
              >
                Save
              </ActionBtnU>
            ) : null}
            {allowEdits ? (
              <FirstBtnContainer>
                <ActionBtnU
                  data-testid="view-btn"
                  borderStyle="1px solid rgb(181, 128, 255)"
                  color="rgb(181, 128, 255)"
                  smallWidth="100%"
                  type="button"
                  onClick={() => setEditModeActive(false)}
                >
                  <span className="mr-2 mt-1">
                    <RiArrowGoBackFill />
                  </span>
                  {/*  */}
                  View
                </ActionBtnU>
                {openButtonIsVisible && (
                  <ActionBtnU
                    data-testid="open-btn"
                    borderStyle="1px solid rgb(181, 128, 255)"
                    color="rgb(181, 128, 255)"
                    disabled={disableOpenButton}
                    type="button"
                    onClick={setOpenStatusDialog}
                  >
                    Reopen
                  </ActionBtnU>
                )}
              </FirstBtnContainer>
            ) : null}
            {!isLocked && (
              <Spacer>
                <ActionBtnU
                  // className="ml-2 mr-2"
                  data-testid="resubmit-btn"
                  type="submit"
                  purpleBg
                  white_color
                  onClick={() => {
                    // Track Resubmit button click in Google Analytics if on Conclusion page
                    const isResubmit = selectedIncident && incidentStatus !== IncidentStatus.Draft;
                    if (tabTitle === "Conclusion" && isResubmit) {
                      trackButtonClick("conclusion_resubmit", { tab: tabTitle });
                    }

                    if (formik) formik.submitForm();
                    if (attachFiles) {
                      attachFiles();
                    }
                  }}
                  disabled={isCreateButtonDisabled}
                >
                  {getSubmitButtonName("var(--primaryWhiteMain)", 24)}
                </ActionBtnU>
              </Spacer>
            )}

            {canUserEditPostSubmission &&
            incidentStatus === IncidentStatus.Draft ? (
              <ActionBtnU
                data-testid="delete-btn"
                className="ml-2 mr-2"
                borderStyle="1px solid rgb(181, 128, 255)"
                color="rgb(181, 128, 255)"
                type="button"
                onClick={() => {
                  if (handleOpenModal) handleOpenModal(true);
                }}
              >
                Delete
              </ActionBtnU>
            ) : null}
          </>
        )}
      </OtherBtnsContainer>
      {showDialog && (
        <AlertDialog
          message={
            "Are you sure you want to delete this incident? You can undo this action if needed. Please confirm."
          }
          onConfirm={_handleDeleteIncident}
          onCancel={() => setShowDialog(false)}
        />
      )}
      {showOpenDialog && (
        <AlertDialog
          message={
            'Moving the incident to "Open" will allow nurses to edit the incident.'
          }
          onConfirm={_setIncidentStatusToOpen}
          onCancel={() => setShowOpenDialog(false)}
        />
      )}
      {showRestore && (
        <AlertDialog
          message="Please confirm that you would like to restore the selected incident."
          onConfirm={_handleRestoreIncident}
          onCancel={() => setShowRestore(false)}
          loading={restoreIncident?.isLoading}
        />
      )}
    </ReportActionBtnContainer>
  );
};

export default ButtonGroup;
